import { Component, inject, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { ActivatedRoute, ActivatedRouteSnapshot, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthLayoutComponent } from '../../components/ui/auth-layout/auth-layout.component';
import { KeycloakAdminService } from '../../services/keycloak/keycloak.service';

function pathOf(snapshot: ActivatedRouteSnapshot): string {
  return (
    '/' +
    snapshot.pathFromRoot
      .flatMap((step) => step.url.map((segment) => segment.path))
      .join('/')
  );
}

interface Destination {
  label: string;
  description: string;
  icon: string;
  link: string;
}

/**
 * Rendered by two routes: the catch-all inside the portal shell
 * (/organization/**) and the root catch-all outside it. Inside, it offers
 * the main console pages; outside, it uses the sign-in layout and leads back
 * to sign-in (or the dashboard when already signed in).
 */
@Component({
  selector: 'notfound',
  standalone: true,
  imports: [RouterLink, AuthLayoutComponent],
  templateUrl: './notfound.component.html',
  styleUrl: './notfound.component.css'
})
export class NotfoundComponent {
  private location = inject(Location);
  private route = inject(ActivatedRoute);
  private injector = inject(Injector);
  private platformId = inject(PLATFORM_ID);

  /** Inside the portal shell (sidebar and top bar around it). */
  readonly inShell = this.route.snapshot.pathFromRoot.some(
    (snapshot) => snapshot.routeConfig?.path === 'organization'
  );

  /** The address that matched nothing, e.g. "/organization/mesages". */
  path = '/';

  readonly destinations: Destination[] = [
    {
      label: 'Dashboard',
      description: 'What’s scheduled, running and recently sent.',
      icon: 'space_dashboard',
      link: '/organization/dashboard',
    },
    {
      label: 'Messages',
      description: 'Compose, schedule and track your SMS.',
      icon: 'sms',
      link: '/organization/messages',
    },
    {
      label: 'Contacts',
      description: 'People, organizations, tags and segments.',
      icon: 'contacts',
      link: '/organization/contacts',
    },
  ];

  /** There is an earlier page in this app to go back to. */
  canGoBack = false;
  signedIn = false;

  constructor() {
    // One unknown URL to another reuses this component (same '**' route), so
    // follow the route's URL instead of reading it once. route.snapshot is
    // already the new one when url emits.
    this.route.url.pipe(takeUntilDestroyed()).subscribe(() => {
      this.path = pathOf(this.route.snapshot);
      if (isPlatformBrowser(this.platformId)) {
        // The router numbers each navigation in history.state; the first page
        // of a visit is 1, so "back" from it would leave the app.
        const state = window.history.state as { navigationId?: number } | null;
        this.canGoBack = (state?.navigationId ?? 1) > 1;
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      // Looked up here, not injected: its storage can't be built on the server.
      this.signedIn = this.injector.get(KeycloakAdminService).hasValidSession;
    }
  }

  goBack(): void {
    this.location.back();
  }
}
